import { CartDishType, CartVendorType, CombinationType } from '../contexts/CartContext';

import { DishType } from '../components/AddDishButton';

interface CartState {
  vendorOnCart: CartVendorType;
  dishesOnCart: CartDishType[];
  notes: string;
  scheduledTime: string;
}

export const initialState: CartState = {
  vendorOnCart: {
    id: '',
    name: '',
  },
  dishesOnCart: [],
  notes: '',
  scheduledTime: '',
};

const addDishToCart: (
  dish: DishType | CartDishType,
  dishesOnCart: CartDishType[],
  combination?: CombinationType
) => CartDishType[] = function(dish, dishesOnCart, combination) {
  const existingDish = dishesOnCart.find(d => d.id === dish.id);

  if (existingDish) {
    existingDish.quantity = existingDish.quantity + 1;
    existingDish.combinations = combination
      ? [...(existingDish.combinations ? existingDish.combinations : []), combination]
      : undefined;
    return dishesOnCart;
  }

  return [
    ...dishesOnCart,
    {
      ...dish,
      quantity: 1,
      combinations: combination ? [combination] : undefined,
    },
  ];
};

const removeDishFromCart: (
  dish: DishType | CartDishType,
  dishesOnCart: CartDishType[],
  modifiedCombinations?: CombinationType[]
) => CartDishType[] = function(dish, dishesOnCart, modifiedCombinations) {
  const existingDish = dishesOnCart.find(d => d.id === dish.id);

  if (!existingDish || existingDish.quantity === 0) return dishesOnCart;

  existingDish.quantity = existingDish.quantity - 1;
  existingDish.combinations = modifiedCombinations;
  return dishesOnCart;
};

const getTotalQuantity: (dishesOnCart: CartDishType[]) => CartDishType[] = function(dishesOnCart) {
  return dishesOnCart.filter(dish => dish.quantity);
};

export interface ActionType {
  type: string;
}

export interface VendorActionType extends ActionType {
  payload: {
    id: string;
    name: string;
  };
}

export interface OrderNotesActionType extends ActionType {
  payload: {
    notes: string;
  };
}

export interface AddDishActionType extends ActionType {
  payload: {
    dish: DishType | CartDishType;
    combination?: CombinationType;
  };
}

export interface RemoveDishActionType extends ActionType {
  payload: {
    dish: DishType | CartDishType;
    modifiedCombinations?: CombinationType[];
  };
}

export interface ClearCartActionType extends ActionType {
  payload: null;
}

function isVendorAction(action: ActionType): action is VendorActionType {
  return action.type === 'VENDOR';
}

function isOrderNotesAction(action: ActionType): action is OrderNotesActionType {
  return action.type === 'ORDER_NOTES';
}

function isAddDishAction(action: ActionType): action is AddDishActionType {
  return action.type === 'ADD_DISH';
}

function isRemoveDishAction(action: ActionType): action is RemoveDishActionType {
  return action.type === 'REMOVE_DISH';
}

function isClearCartAction(action: ActionType): action is ClearCartActionType {
  return action.type === 'CLEAR_CART';
}

const reducer = (state: CartState, action: ActionType) => {
  if (isClearCartAction(action)) {
    return {
      ...state,
      vendorOnCart: { id: '', name: '' },
      dishesOnCart: [],
      notes: '',
      scheduledTime: '',
    };
  }
  if (isVendorAction(action)) {
    return {
      ...state,
      vendorOnCart: { id: action.payload.id, name: action.payload.name },
      dishesOnCart: [],
    };
  }
  if (isOrderNotesAction(action)) {
    return {
      ...state,
      notes: action.payload.notes,
    };
  }
  if (isAddDishAction(action)) {
    const updatedDishes = addDishToCart(
      action.payload.dish,
      state.dishesOnCart,
      action.payload.combination
    );
    return {
      ...state,
      dishesOnCart: getTotalQuantity(updatedDishes),
    };
  }
  if (isRemoveDishAction(action)) {
    const updatedDishes = removeDishFromCart(
      action.payload.dish,
      state.dishesOnCart,
      action.payload.modifiedCombinations
    );
    return {
      ...state,
      dishesOnCart: getTotalQuantity(updatedDishes),
    };
  }
  return state;
};

export default reducer;
